import { useState, useCallback } from 'react'
import type { Store, Girl, Package, Customer, Tag, Order, CustomerAccount } from '@/types'
import { useAppStore } from '@/stores/appStore'

const API_BASE = 'https://cs-order-api.550759734-d15.workers.dev/api'

// 通用fetch函数（带认证）
async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // 从 localStorage 获取 token
  const storageData = localStorage.getItem('app-storage')
  let token: string | null = null
  if (storageData) {
    try {
      const parsed = JSON.parse(storageData)
      token = parsed?.state?.token || null
    } catch {}
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers,
    ...options,
  })

  // 401 时清除登录状态
  if (response.status === 401) {
    const store = useAppStore.getState()
    store.clearAuth()
    throw new Error('登录已过期，请重新登录')
  }

  const data = await response.json()
  if (!data.success) {
    throw new Error(data.error || 'API Error')
  }
  return data.data
}

// 统一的 useApi hook
export function useApi() {
  const [error, setError] = useState<string | null>(null)

  // Stores
  const getStores = useCallback(async (): Promise<Store[]> => {
    return fetchApi('/stores')
  }, [])

  const createStore = useCallback(async (data: { name: string }): Promise<Store> => {
    return fetchApi('/stores', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updateStore = useCallback(async (id: string, data: Partial<Store>): Promise<Store> => {
    return fetchApi(`/stores?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  // Girls
  const getGirls = useCallback(async (storeId: string): Promise<Girl[]> => {
    return fetchApi(`/girls?storeId=${storeId}`)
  }, [])

  const createGirl = useCallback(async (data: {
    storeId: string
    name: string
    status: string
    commissionType: string
    commissionValue: number
  }): Promise<Girl> => {
    return fetchApi('/girls', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updateGirl = useCallback(async (id: string, data: Partial<Girl>): Promise<Girl> => {
    return fetchApi(`/girls?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  const deleteGirl = useCallback(async (id: string): Promise<void> => {
    return fetchApi(`/girls?id=${id}`, { method: 'DELETE' })
  }, [])

  // Packages
  const getPackages = useCallback(async (storeId: string): Promise<Package[]> => {
    return fetchApi(`/packages?storeId=${storeId}`)
  }, [])

  const createPackage = useCallback(async (data: {
    storeId: string
    name: string
    basePrice: number
  }): Promise<Package> => {
    return fetchApi('/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updatePackage = useCallback(async (id: string, data: Partial<Package>): Promise<Package> => {
    return fetchApi(`/packages?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  const deletePackage = useCallback(async (id: string): Promise<void> => {
    return fetchApi(`/packages?id=${id}`, { method: 'DELETE' })
  }, [])

  // Tags
  const getTags = useCallback(async (storeId: string): Promise<Tag[]> => {
    return fetchApi(`/tags?storeId=${storeId}`)
  }, [])

  const createTag = useCallback(async (data: {
    storeId: string
    name: string
    color: string
  }): Promise<Tag> => {
    return fetchApi('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updateTag = useCallback(async (id: string, data: Partial<Tag>): Promise<Tag> => {
    return fetchApi(`/tags?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  const deleteTag = useCallback(async (id: string): Promise<void> => {
    return fetchApi(`/tags?id=${id}`, { method: 'DELETE' })
  }, [])

  // Customers
  const getCustomers = useCallback(async (storeId: string): Promise<(Customer & { accounts?: CustomerAccount[]; tagIds?: string[] })[]> => {
    return fetchApi(`/customers?storeId=${storeId}`)
  }, [])

  const createCustomer = useCallback(async (data: {
    storeId: string
    name: string
    accounts?: { platform: string; accountId: string; note?: string }[]
    tagIds?: string[]
  }): Promise<{ id: string; accounts?: { id: string; platform: string; account: string }[] }> => {
    return fetchApi('/customers', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updateCustomer = useCallback(async (id: string, data: Partial<Customer & { accounts?: { platform: string; accountId: string; note?: string }[]; tagIds?: string[] }>): Promise<Customer> => {
    return fetchApi(`/customers?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  const deleteCustomer = useCallback(async (id: string): Promise<void> => {
    return fetchApi(`/customers?id=${id}`, { method: 'DELETE' })
  }, [])

  // Orders
  const getOrders = useCallback(async (storeId: string): Promise<Order[]> => {
    return fetchApi(`/orders?storeId=${storeId}`)
  }, [])

  const createOrder = useCallback(async (data: {
    storeId: string
    customerId: string
    customerAccountId?: string
    girlId: string
    packageId: string
    price: number
    discount?: number
    appointmentTime?: string
    hours?: number
    originalPrice?: number
    totalOriginalAmount?: number
    discountType?: 'memberDay' | 'memberRegular' | 'coupon' | 'freeOrder' | 'trial' | 'none'
    discountPercent?: number
    discountAmount?: number
    deductedBalance?: number
    usedMemberDayBenefit?: number
  }): Promise<Order> => {
    return fetchApi('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const updateOrder = useCallback(async (id: string, data: Partial<Order>): Promise<Order> => {
    return fetchApi(`/orders?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }, [])

  const deleteOrder = useCallback(async (id: string): Promise<void> => {
    return fetchApi(`/orders?id=${id}`, { method: 'DELETE' })
  }, [])

  const exportOrders = useCallback(async (data: {
    storeId: string
    startDate: string
    endDate: string
    status?: string
  }): Promise<any[]> => {
    return fetchApi('/orders/export', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  // Dashboard
  const getDashboard = useCallback(async (storeId: string): Promise<{
    todayRevenue: number
    todayOrders: number
    todayCompleted: number
    todayCancelled: number
    monthRevenue: number
    monthOrders: number
    monthCompleted: number
    monthCancelled: number
    monthServiceCommission: number
    totalCustomers: number
    newCustomersThisMonth: number
    girlRanking: { id: string; name: string; orderCount: number; revenue: number; girlIncome: number }[]
    customerRanking: { id: string; name: string; orderCount: number; revenue: number }[]
    tagStats: { id: string; name: string; color: string | null; count: number }[]
  }> => {
    return fetchApi(`/dashboard?storeId=${storeId}`)
  }, [])

  // Girl Package Prices
  const getGirlPackagePrices = useCallback(async (girlId: string): Promise<{ packageId: string; price: number; dailyPrice?: number | null; packageName: string; packageCode: string }[]> => {
    return fetchApi(`/girl-package-prices?girlId=${girlId}`)
  }, [])

  const saveGirlPackagePrice = useCallback(async (data: {
    storeId: string
    girlId: string
    packageId: string
    price: number
    dailyPrice?: number | null
  }): Promise<{ id: string }> => {
    return fetchApi('/girl-package-prices', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  // Member Config
  const getMemberConfig = useCallback(async (storeId: string): Promise<{
    enabled: boolean
    priceMarkup: number
    levels: { level: number; name: string; minRecharge: number; regularDiscount: number; memberDayDiscount: number }[]
    memberDays: number[]
    minBalancePercent: number
  }> => {
    return fetchApi(`/member-config?storeId=${storeId}`)
  }, [])

  const saveMemberConfig = useCallback(async (data: {
    storeId: string
    enabled: boolean
    priceMarkup: number
    levels: { level: number; name: string; minRecharge: number; regularDiscount: number; memberDayDiscount: number }[]
    memberDays: number[]
    minBalancePercent: number
  }): Promise<{ id: string }> => {
    return fetchApi('/member-config', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  // Recharge
  const recharge = useCallback(async (data: {
    customerId: string
    storeId: string
    amount: number
    giftAmount?: number
    remark?: string
  }): Promise<{
    rechargeId: string
    beforeLevel: number
    afterLevel: number
    beforeBalance: number
    afterBalance: number
    addedAmount: number
  }> => {
    return fetchApi('/recharge', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  const getRechargeHistory = useCallback(async (customerId: string): Promise<any[]> => {
    return fetchApi(`/recharge/history?customerId=${customerId}`)
  }, [])

  const getBalanceTransactions = useCallback(async (customerId: string): Promise<any[]> => {
    return fetchApi(`/recharge/transactions?customerId=${customerId}`)
  }, [])

  // Analysis
  const getCustomerPreferences = useCallback(async (storeId: string, range: string): Promise<{
    customerRankings: any[]
    girlPreferences: any[]
    packagePreferences: any[]
    inactiveCustomers: any[]
  }> => {
    return fetchApi(`/analysis/customer-preferences?storeId=${storeId}&range=${range}`)
  }, [])

  const getCustomerDetail = useCallback(async (storeId: string, customerId: string): Promise<any> => {
    return fetchApi(`/analysis/customer-detail?storeId=${storeId}&customerId=${customerId}`)
  }, [])

  // Trends
  const getGirlTrends = useCallback(async (storeId: string, range: string): Promise<{
    trendData: any[]
    girlLegend: any[]
    groupBy: 'day' | 'week' | 'month'
  }> => {
    return fetchApi(`/trends/girl-trends?storeId=${storeId}&range=${range}`)
  }, [])

  // Daily Report
  const getDailyReport = useCallback(async (storeId: string, date: string): Promise<{
    summary: any
    girlStats: any[]
    orders: any[]
  }> => {
    return fetchApi(`/daily-report?storeId=${storeId}&date=${date}`)
  }, [])

  // Order Calculation
  const calculateOrderPrice = useCallback(async (data: {
    storeId: string
    customerId: string
    girlId: string
    packageId: string
    hours?: number
    date?: string
  }): Promise<{
    basePrice: number
    hours: number
    totalOriginalAmount: number
    priceMarkup?: number
    discountType: 'memberDay' | 'memberRegular' | 'trial' | 'none'
    discountPercent: number
    discountAmount: number
    finalPrice: number
    deductedBalance: number
    breakdown?: { hour: number; originalPrice: number; discountPercent: number; finalPrice: number; type: string }[]
    girlIncome: number
    serviceCommission: number
    usedMemberDayBenefit: boolean
    reason: string
  }> => {
    return fetchApi('/calculate-price', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }, [])

  return {
    error,
    setError,
    // Stores
    getStores,
    createStore,
    updateStore,
    // Girls
    getGirls,
    createGirl,
    updateGirl,
    deleteGirl,
    // Packages
    getPackages,
    createPackage,
    updatePackage,
    deletePackage,
    // Tags
    getTags,
    createTag,
    updateTag,
    deleteTag,
    // Customers
    getCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    // Orders
    getOrders,
    createOrder,
    updateOrder,
    deleteOrder,
    exportOrders,
    // Dashboard
    getDashboard,
    // Girl Package Prices
    getGirlPackagePrices,
    saveGirlPackagePrice,
    // Member Config
    getMemberConfig,
    saveMemberConfig,
    // Recharge
    recharge,
    getRechargeHistory,
    getBalanceTransactions,
    // Order Calculation
    calculateOrderPrice,
    // Analysis
    getCustomerPreferences,
    getCustomerDetail,
    // Trends
    getGirlTrends,
    // Daily Report
    getDailyReport,
  }
}

// React Query hooks (保留供其他用途)
export * from './useQueryHooks'
